import type { StaffUserDto } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { MoreHorizontal, UsersRound } from 'lucide-react';
import { useStaffManagementContent } from '../../content/sections/staff-management.js';
import { fmtDate } from '../../lib/format.js';
import { staffRoleLabel, staffStatusLabel } from './staff-labels.js';

export type StaffRowAction = 'role' | 'reset-password' | 'suspend' | 'reactivate';

interface Props {
  readonly items: readonly StaffUserDto[];
  /** id do operador da sessão — o self-suspend é escondido (o backend também barra). */
  readonly currentStaffId: string | null;
  readonly onAction: (staff: StaffUserDto, action: StaffRowAction) => void;
  readonly onCreate: () => void;
  readonly selectedIds?: ReadonlySet<string>;
  readonly onToggle?: (id: string) => void;
  readonly onToggleAll?: (checked: boolean) => void;
}

/* Tabela de operadores sobre o primitive `Table` de @planuze/ui (ADR-0700/0709). Papel = badge
   neutro; status = StatusBadge (ativo=success, suspenso=neutral). Ações por linha num
   DropdownMenu (trocar papel, suspender/reativar, resetar senha). */
export function StaffTable({
  items,
  currentStaffId,
  onAction,
  onCreate,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = useStaffManagementContent();
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<UsersRound className="size-6" />}
        title={copy.table.emptyTitle}
        description={copy.table.emptyDescription}
        primaryAction={<Button onClick={onCreate}>{copy.table.createButton}</Button>}
      />
    );
  }
  const selection = selectedIds ?? new Set<string>();
  const bulkEnabled = selectedIds !== undefined;
  const eligible = items.filter((staff) => staff.id !== currentStaffId);
  const allSelected = eligible.length > 0 && eligible.every((staff) => selection.has(staff.id));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {bulkEnabled && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={eligible.length === 0}
                onCheckedChange={(checked) => onToggleAll?.(checked === true)}
                aria-label={copy.table.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.table.columnEmail}</TableHead>
          <TableHead>{copy.table.columnRole}</TableHead>
          <TableHead>{copy.table.columnStatus}</TableHead>
          <TableHead>{copy.table.columnCreatedAt}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((staff) => {
          const isSelf = staff.id === currentStaffId;
          const suspended = staff.status === 'suspended';
          return (
            <TableRow key={staff.id}>
              {bulkEnabled && (
                <TableCell>
                  <Checkbox
                    checked={selection.has(staff.id)}
                    disabled={isSelf}
                    onCheckedChange={() => onToggle?.(staff.id)}
                    aria-label={copy.table.selectRowAriaLabel(staff.email)}
                  />
                </TableCell>
              )}
              <TableCell className="text-foreground">{staff.email}</TableCell>
              <TableCell>
                <StatusBadge variant="neutral">
                  {staffRoleLabel(staff.role, copy.roleLabels)}
                </StatusBadge>
              </TableCell>
              <TableCell>
                <StatusBadge variant={suspended ? 'neutral' : 'success'} dot>
                  {staffStatusLabel(staff.status, copy.statusLabels)}
                </StatusBadge>
              </TableCell>
              <TableCell className="text-muted-foreground">{fmtDate(staff.createdAt)}</TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={copy.table.actionsAriaLabel(staff.email)}
                    >
                      <MoreHorizontal className="size-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => onAction(staff, 'role')}>
                      {copy.table.changeRoleAction}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAction(staff, 'reset-password')}>
                      {copy.table.resetPasswordAction}
                    </DropdownMenuItem>
                    {suspended ? (
                      <DropdownMenuItem onClick={() => onAction(staff, 'reactivate')}>
                        {copy.table.reactivateAction}
                      </DropdownMenuItem>
                    ) : (
                      !isSelf && (
                        <DropdownMenuItem onClick={() => onAction(staff, 'suspend')}>
                          {copy.table.suspendAction}
                        </DropdownMenuItem>
                      )
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
